import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useEmpresaLayout } from '@/hooks/useEmpresaLayout';
import { menuGroups } from '@/app/registry/menu';
import type { MenuGroup, MenuItem, SubMenuItem } from '@/app/registry/menu';

type NavItem = MenuItem | SubMenuItem;

const hasAnyPermission = (permission: string | string[], hasPermission: (permission: string) => boolean) =>
  Array.isArray(permission) ? permission.some((item) => hasPermission(item)) : hasPermission(permission);

const isContainer = (item: NavItem) => item.href === '#';

const filterSubItems = (items: SubMenuItem[], hasPermission: (permission: string) => boolean): SubMenuItem[] =>
  items
    .map((item) => {
      const subItems = item.subItems ? filterSubItems(item.subItems, hasPermission) : undefined;
      const allowed = hasAnyPermission(item.permission, hasPermission);
      const include = isContainer(item) ? allowed || Boolean(subItems?.length) : allowed || Boolean(subItems?.length);
      return include ? { ...item, ...(subItems ? { subItems } : {}) } : null;
    })
    .filter(Boolean) as SubMenuItem[];

const isItemActive = (pathname: string, item: NavItem): boolean => {
  if (item.href !== '#' && pathname === item.href) return true;
  return item.subItems?.some((subItem) => isItemActive(pathname, subItem)) ?? false;
};

const getItemKey = (parents: string[], item: NavItem) => [...parents, item.name].join('::');

const linkClassName = (active: boolean, nestedLevel = 0) =>
  cn(
    'flex min-w-0 items-center rounded-md text-sm font-medium transition-colors',
    nestedLevel === 0 ? 'px-3 py-2' : 'px-3 py-1.5',
    active
      ? 'bg-white text-[var(--brand-charcoal)]'
      : 'text-[rgba(255,255,255,0.72)] hover:bg-[rgba(255,255,255,0.08)] hover:text-white',
  );

const NavBranch = ({
  collapsed,
  expanded,
  item,
  level = 0,
  onExpandSidebar,
  onNavigate,
  parents,
  pathname,
  toggleExpanded,
}: {
  collapsed?: boolean;
  expanded: Record<string, boolean>;
  item: NavItem;
  level?: number;
  /** Menu recolhido: abrir um grupo expande o menu, senão os itens dele não aparecem. */
  onExpandSidebar?: () => void;
  onNavigate?: () => void;
  parents: string[];
  pathname: string;
  toggleExpanded: (key: string) => void;
}) => {
  const key = getItemKey(parents, item);
  const active = isItemActive(pathname, item);
  const hasChildren = Boolean(item.subItems?.length);
  const isOpen = expanded[key] ?? active;

  if (collapsed && level > 0) return null;

  if (hasChildren || isContainer(item)) {
    return (
      <div>
        <button
          onClick={() => {
            if (collapsed) {
              onExpandSidebar?.();
              if (!isOpen) toggleExpanded(key);
              return;
            }
            toggleExpanded(key);
          }}
          className={cn(linkClassName(active, level), 'w-full justify-between', collapsed && 'justify-center px-2')}
          title={item.name}
        >
          <span className="flex min-w-0 items-center gap-2">
            {level > 0 ? <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-50" /> : null}
            {'icon' in item && item.icon ? <item.icon className="h-4 w-4 shrink-0" /> : null}
            {!collapsed ? <span className="truncate">{item.name}</span> : null}
          </span>
          {!collapsed ? isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" /> : null}
        </button>
        {!collapsed && isOpen && item.subItems ? (
          <div className={cn('mt-1 space-y-1', level === 0 ? 'ml-4' : 'ml-3')}>
            {item.subItems.map((subItem) => (
              <NavBranch
                key={getItemKey([...parents, item.name], subItem)}
                expanded={expanded}
                item={subItem}
                level={level + 1}
                onNavigate={onNavigate}
                parents={[...parents, item.name]}
                pathname={pathname}
                toggleExpanded={toggleExpanded}
              />
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      className={cn(linkClassName(active, level), 'gap-2', collapsed && 'justify-center px-2')}
      title={item.name}
    >
      {level > 0 ? <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-50" /> : null}
      {'icon' in item && item.icon ? <item.icon className="h-4 w-4 shrink-0" /> : null}
      {!collapsed ? <span className="truncate">{item.name}</span> : null}
    </Link>
  );
};

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { hasPermission } = useAuth();
  const { layoutConfig } = useEmpresaLayout();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(false);

  const filteredMenuGroups = useMemo<MenuGroup[]>(() => {
    return menuGroups
      .map((group) => ({
        ...group,
        items: group.items
          .map((item) => {
            const subItems = item.subItems ? filterSubItems(item.subItems, hasPermission) : undefined;
            const allowed = hasAnyPermission(item.permission, hasPermission);
            const include = isContainer(item) ? allowed || Boolean(subItems?.length) : allowed || Boolean(subItems?.length);
            return include ? { ...item, ...(subItems ? { subItems } : {}) } : null;
          })
          .filter(Boolean) as MenuItem[],
      }))
      .filter((group) => group.items.length > 0);
  }, [hasPermission]);

  const toggleExpanded = (key: string) => {
    setExpanded((current) => ({ ...current, [key]: !current[key] }));
  };

  const closeMobile = () => setIsMobileOpen(false);

  return (
    <>
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="fixed left-4 top-4 z-50 rounded-md border border-[var(--app-hairline)] bg-[var(--brand-charcoal)] p-2 text-white shadow-lg lg:hidden"
        title={isMobileOpen ? 'Fechar menu' : 'Abrir menu'}
      >
        {isMobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {isMobileOpen ? <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={closeMobile} /> : null}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col overflow-hidden bg-[var(--brand-charcoal)] text-white shadow-lg transition-all duration-300 ease-in-out lg:static lg:translate-x-0',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full',
          isDesktopCollapsed && 'lg:w-[var(--sidebar-w-collapsed)]',
        )}
      >
        <div
          className={cn(
            'flex h-[var(--app-topbar-h)] shrink-0 items-center border-b border-[var(--brand-hairline)]',
            isDesktopCollapsed ? 'justify-center px-2' : 'justify-between px-4',
          )}
        >
          {!isDesktopCollapsed ? (
            layoutConfig?.logo_url ? (
              <img
                src={layoutConfig.logo_url}
                alt={layoutConfig.nome_empresa || 'Logo da empresa'}
                className="h-8 max-w-[160px] object-contain"
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{layoutConfig?.nome_empresa || 'Grupo Prospera'}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--brand-ink-2)]">Portal de Tributos</div>
              </div>
            )
          ) : null}
          <button
            onClick={() => setIsDesktopCollapsed((current) => !current)}
            className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[var(--brand-hairline)] text-[var(--brand-ink-2)] hover:bg-[rgba(255,255,255,0.08)] hover:text-white lg:flex"
            title={isDesktopCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {isDesktopCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        <nav className="barra-rolagem-escura min-h-0 flex-1 overflow-y-auto px-3 py-3">
          <div className="space-y-1">
            {filteredMenuGroups.map((group) => {
              const groupOpen = group.flat || (expanded[group.name] ?? group.items.some((item) => isItemActive(location.pathname, item)));

              return (
                <div key={group.name} className="pt-2">
                  {!isDesktopCollapsed && !group.flat ? (
                    <button
                      onClick={() => toggleExpanded(group.name)}
                      className="flex w-full items-center justify-between px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-[var(--brand-ink-3)] hover:text-[var(--brand-ink-2)]"
                    >
                      <span>{group.name}</span>
                      {groupOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    </button>
                  ) : null}

                  {(groupOpen || isDesktopCollapsed) && (
                    <div className="mt-1 space-y-1">
                      {group.items.map((item) => (
                        <NavBranch
                          key={getItemKey([group.name], item)}
                          collapsed={isDesktopCollapsed}
                          expanded={expanded}
                          item={item}
                          onExpandSidebar={() => setIsDesktopCollapsed(false)}
                          onNavigate={closeMobile}
                          parents={[group.name]}
                          pathname={location.pathname}
                          toggleExpanded={toggleExpanded}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
